// Formatierter Text für Rückfragen, Aufgaben und Kommentare (Darstellung: RichText.tsx). Bewusst klein und sicher:
// kein HTML, nur Klartext mit wenigen Regeln – System-Texte nutzen sie, Menschen dürfen sie nutzen, müssen aber nicht.
//   ## Überschrift                     → Abschnittstitel
//   - Punkt / • Punkt / 1. Punkt       → Liste (eingerückte Folgezeilen gehören zum Punkt)
//   Feld: Wert                         → Faktenliste in zwei Spalten
//   Nachricht: (ohne Wert) + Text      → Zitat
//   Bitte … / Nächster Schritt: …      → Handlungsaufforderung („Was ist zu tun?“)
//   **fett**, [Text](/leads/…), https://… → Hervorhebung und Links (nur http(s) und Pfade der Anwendung)
//   Diagnose und Kopfzeilen von Mailservern („Original message headers“, ARC-/DKIM-Signaturen …) → eingeklappt
//   („Technische Angaben“) – gleiche Regel wie email.HumanPart im Backend.

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; text: string }
  | { kind: 'link'; text: string; href: string; auto: boolean };
export type Line = Inline[];
export type Block =
  | { kind: 'heading'; line: Line }
  | { kind: 'paragraph'; lines: Line[]; leadIn: boolean }
  | { kind: 'facts'; items: { label: string; value: Line }[] }
  | { kind: 'quote'; label: string; lines: Line[] }
  | { kind: 'list'; ordered: boolean; start: number; items: Line[][] }
  | { kind: 'action'; lines: Line[] }
  | { kind: 'technical'; lines: string[] };

const headingRe = /^#{1,3}\s+(.+)$/;
const bulletRe = /^\s{0,3}[-•*]\s+(.+)$/;
const numberRe = /^\s{0,3}(\d{1,3})[.)]\s+(.+)$/;
const continuationRe = /^\s{2,}\S/;
const factRe = /^([A-ZÄÖÜ][^:]{0,40}?):\s+(\S.*)$/;
const labelOnlyRe = /^([A-ZÄÖÜ][^:]{0,40}):$/;
const actionRe = /^(Bitte\s|Nächster Schritt:|Was ist zu tun)/;
const technicalStartRe =
  /^\s*((original message headers|diagnostic information for administrators|ursprüngliche nachrichtenkopfzeilen|diagnoseinformationen für administratoren)\s*:?\s*$|(arc-[a-z-]+|dkim-[a-z-]+|received(-spf)?|authentication-results|x-[a-z0-9-]+|message-id|mime-version|content-transfer-encoding|return-path|thread-index)\s*:)/i;
const inlineRe = /\*\*(.+?)\*\*|\[([^\]\n]+)\]\(((?:https?:\/\/|\/)[^\s)]+)\)|(https?:\/\/[^\s<>()]*[^\s<>().,;:!?'"])/g;

// Ein Feldname ist kurz und kein Satz (höchstens fünf Wörter, keine Adresse).
const isLabel = (label: string) => label.trim().split(/\s+/).length <= 5 && !/https?$/i.test(label);

export function inline(text: string): Line {
  const out: Line = [];
  let last = 0;
  for (const m of text.matchAll(inlineRe)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'text', text: text.slice(last, at) });
    if (m[1] !== undefined) out.push({ kind: 'strong', text: m[1] });
    else if (m[2] !== undefined && m[3] !== undefined) out.push({ kind: 'link', text: m[2], href: m[3], auto: false });
    else if (m[4] !== undefined) out.push({ kind: 'link', text: m[4], href: m[4], auto: true });
    last = at + m[0].length;
  }
  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

function classify(lines: string[]): Block[] {
  const first = lines[0] ?? '';
  const label = labelOnlyRe.exec(first);
  if (label?.[1] && lines.length > 1 && isLabel(label[1])) return [{ kind: 'quote', label: label[1], lines: lines.slice(1).map(inline) }];
  if (actionRe.test(first)) return [{ kind: 'action', lines: lines.map((l, i) => inline(i === 0 ? l.replace(/^Nächster Schritt:\s*/, '') : l)) }];
  const out: Block[] = [];
  let facts: { label: string; value: Line }[] = [];
  let para: string[] = [];
  const flushFacts = () => {
    if (facts.length) out.push({ kind: 'facts', items: facts });
    facts = [];
  };
  const flushPara = () => {
    if (para.length) out.push({ kind: 'paragraph', lines: para.map(inline), leadIn: false });
    para = [];
  };
  for (const l of lines) {
    const f = factRe.exec(l);
    if (f?.[1] && f[2] && isLabel(f[1])) {
      flushPara();
      facts.push({ label: f[1], value: inline(f[2]) });
    } else {
      flushFacts();
      para.push(l);
    }
  }
  flushPara();
  flushFacts();
  return out;
}

export function parseRichText(text: string | null | undefined): Block[] {
  const blocks: Block[] = [];
  let group: string[] = [];
  let list: { ordered: boolean; start: number; items: string[][] } | null = null;
  const flushGroup = () => {
    if (group.length) blocks.push(...classify(group));
    group = [];
  };
  const flushList = () => {
    if (list) blocks.push({ kind: 'list', ordered: list.ordered, start: list.start, items: list.items.map((item) => item.map(inline)) });
    list = null;
  };
  const all = (text ?? '').replace(/\r\n?/g, '\n').split('\n');
  const cut = all.findIndex((l) => technicalStartRe.test(l));
  const technical = cut < 0 ? [] : all.slice(cut);
  for (const raw of cut < 0 ? all : all.slice(0, cut)) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushGroup();
      flushList();
      continue;
    }
    const heading = headingRe.exec(line);
    if (heading?.[1]) {
      flushGroup();
      flushList();
      blocks.push({ kind: 'heading', line: inline(heading[1]) });
      continue;
    }
    const bullet = bulletRe.exec(line);
    const number = bullet ? null : numberRe.exec(line);
    if (bullet?.[1] || (number?.[1] && number[2])) {
      flushGroup();
      const ordered = !bullet;
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, start: number ? Number(number[1]) : 1, items: [] };
      }
      list.items.push([bullet?.[1] ?? number?.[2] ?? '']);
      continue;
    }
    if (list && continuationRe.test(line)) {
      list.items[list.items.length - 1]?.push(line.trim());
      continue;
    }
    flushList();
    group.push(line.trim());
  }
  flushGroup();
  flushList();
  if (technical.some((l) => l.trim())) blocks.push({ kind: 'technical', lines: technical });
  // Ein einzeiliger Absatz mit Doppelpunkt direkt vor einer Liste ist deren Überschrift.
  blocks.forEach((b, i) => {
    if (b.kind === 'paragraph' && b.lines.length === 1 && blocks[i + 1]?.kind === 'list' && lineText(b.lines[0] ?? []).endsWith(':')) b.leadIn = true;
  });
  return blocks;
}

export const lineText = (l: Line) => l.map((x) => x.text).join('');

// extractAction: die erste Handlungsaufforderung – die Detailseite zeigt sie oben als „Was ist zu tun?“.
export function extractAction(blocks: Block[]): { action: Extract<Block, { kind: 'action' }> | null; rest: Block[] } {
  const i = blocks.findIndex((b) => b.kind === 'action');
  const action = blocks[i];
  if (i < 0 || action?.kind !== 'action') return { action: null, rest: blocks };
  return { action, rest: blocks.filter((_, j) => j !== i) };
}

// previewText: eine Zeile für Listen – der erste Satz bzw. die ersten Fakten, ohne Auszeichnung.
export function previewText(text: string | null | undefined, max = 180): string {
  // Technische Angaben (Diagnose, Kopfzeilen) erscheinen nie in der Vorschau.
  const first = parseRichText(text).find((b) => b.kind !== 'heading' && b.kind !== 'technical');
  if (!first) return '';
  let s: string;
  if (first.kind === 'facts') s = first.items.slice(0, 2).map((f) => `${f.label}: ${lineText(f.value)}`).join(' · ');
  else if (first.kind === 'list') s = lineText(first.items[0]?.[0] ?? []);
  else s = lineText(first.lines[0] ?? []);
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

// internalPath: Pfad in der Anwendung für einen Link („/leads/…“ wird um das Projekt ergänzt), sonst null (extern).
export function internalPath(href: string, projectKey: string, origin: string): string | null {
  if (href.startsWith('/')) return href.startsWith('/p/') ? href : `/p/${projectKey}${href}`;
  try {
    const u = new URL(href);
    return u.origin === origin ? u.pathname + u.search + u.hash : null;
  } catch {
    return null;
  }
}

// linkLabel: lesbarer Text für eingefügte Adressen („Lead öffnen“ statt einer langen Adresse).
export function linkLabel(path: string | null, href: string): string {
  if (path && /\/leads\/[^/?#]+$/.test(path)) return 'Lead öffnen';
  if (path && /\/termine\/[^/?#]+$/.test(path)) return 'Termin öffnen';
  if (path && /\/tickets\/[^/?#]+$/.test(path)) return 'Vorgang öffnen';
  if (path) return path;
  try {
    const u = new URL(href);
    return u.host + (u.pathname.length > 1 ? u.pathname : '');
  } catch {
    return href;
  }
}
